/**
 * Deterministic pass/fail assertions for prompt unit tests.
 * Every assertion is a pure function of (spec, model output) — no network,
 * no randomness, no model-in-the-loop judging.
 */

import type { AssertionSpec, AssertionResult } from './types';

export type { AssertionSpec, AssertionResult };

/** Label shown in the UI and in generated reports. */
export function formatAssertion(spec: Pick<AssertionSpec, 'type' | 'keys' | 'pattern' | 'min' | 'max'>): string {
  switch (spec.type) {
    case 'json':
      return 'Must be valid JSON';
    case 'json-keys':
      return `JSON must contain keys: ${(spec.keys ?? []).join(', ')}`;
    case 'not-regex':
      return `Must NOT match regex: ${spec.pattern ?? ''}`;
    case 'word-count':
      return `Word count between ${spec.min ?? 0} and ${spec.max ?? Number.MAX_SAFE_INTEGER}`;
    default:
      return 'Unknown assertion';
  }
}

/** Strip ``` fences / leading prose so JSON extraction behaves like a human would. */
export function extractJsonCandidate(output: string): string {
  const fenced = output.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced) return fenced[1].trim();
  const firstBrace = output.search(/[[{]/);
  if (firstBrace === -1) return output.trim();
  return output.slice(firstBrace).trim();
}

export function parseJson(output: string): { ok: boolean; value?: unknown; error?: string } {
  const candidate = extractJsonCandidate(output);
  try {
    return { ok: true, value: JSON.parse(candidate) };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/** Resolve `a.b.c` paths against a parsed JSON value. */
export function getPath(value: unknown, path: string): unknown {
  let cursor: unknown = value;
  for (const segment of path.split('.')) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/**
 * Parse a regex from user input.
 * Accepts both `/pattern/flags` and bare `pattern` (case-insensitive by default
 * for bare patterns so `/sorry|as an AI/i` and `sorry|as an AI` behave alike).
 */
export function parseRegex(input: string): { ok: true; regex: RegExp } | { ok: false; error: string } {
  const trimmed = (input ?? '').trim();
  if (!trimmed) return { ok: false, error: 'Empty regex' };

  const delimited = trimmed.match(/^\/(.+)\/([gimsuy]*)$/);
  try {
    if (delimited) return { ok: true, regex: new RegExp(delimited[1], delimited[2].replace('g', '')) };
    return { ok: true, regex: new RegExp(trimmed, 'i') };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Invalid regex' };
  }
}

export function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

function result(spec: AssertionSpec, pass: boolean, detail: string): AssertionResult {
  return { assertionId: spec.id, label: formatAssertion(spec), pass, detail };
}

/** Evaluate one assertion against a model output. */
export function evaluateAssertion(spec: AssertionSpec, output: string): AssertionResult {
  switch (spec.type) {
    case 'json': {
      const parsed = parseJson(output ?? '');
      if (parsed.ok) return result(spec, true, 'Parsed JSON successfully');
      return result(spec, false, `Invalid JSON: ${parsed.error}`);
    }

    case 'json-keys': {
      const required = spec.keys ?? [];
      if (required.length === 0) return result(spec, false, 'No keys configured');
      const parsed = parseJson(output ?? '');
      if (!parsed.ok) return result(spec, false, `Output is not valid JSON: ${parsed.error}`);
      const missing = required.filter((key) => getPath(parsed.value, key) === undefined);
      if (missing.length === 0) return result(spec, true, `All keys present: ${required.join(', ')}`);
      return result(spec, false, `Missing key(s): ${missing.join(', ')}`);
    }

    case 'not-regex': {
      const parsed = parseRegex(spec.pattern ?? '');
      if (!parsed.ok) return result(spec, false, `Bad regex — ${parsed.error}`);
      const match = parsed.regex.exec(output ?? '');
      if (match) {
        return result(spec, false, `Matched forbidden pattern: "${match[0]}"`);
      }
      return result(spec, true, 'No forbidden pattern found');
    }

    case 'word-count': {
      const min = spec.min ?? 0;
      const max = spec.max ?? Number.MAX_SAFE_INTEGER;
      const words = countWords(output ?? '');
      if (words < min) return result(spec, false, `${words} words is below minimum ${min}`);
      if (words > max) return result(spec, false, `${words} words exceeds maximum ${max}`);
      return result(spec, true, `${words} words within [${min}, ${max}]`);
    }

    default:
      return result(spec, false, `Unsupported assertion type: ${String((spec as AssertionSpec).type)}`);
  }
}

/** Evaluate every assertion on a test-case row. */
export function evaluateAll(
  specs: AssertionSpec[],
  output: string
): { results: AssertionResult[]; passed: number; total: number; pass: boolean } {
  const results = specs.map((spec) => evaluateAssertion(spec, output));
  const passed = results.filter((r) => r.pass).length;
  return { results, passed, total: results.length, pass: results.length > 0 && passed === results.length };
}

/** Convenience constructor used by the UI when adding a new assertion. */
export function makeAssertion(type: AssertionSpec['type'], preset: Partial<AssertionSpec> = {}): AssertionSpec {
  const base: AssertionSpec = { id: `${type}_${Math.random().toString(36).slice(2, 9)}`, type };
  switch (type) {
    case 'json':
      return base;
    case 'json-keys':
      return { ...base, keys: preset.keys?.length ? preset.keys : ['answer'] };
    case 'not-regex':
      return { ...base, pattern: preset.pattern ?? '/sorry|as an AI|cannot/i' };
    case 'word-count':
      return { ...base, min: preset.min ?? 5, max: preset.max ?? 120 };
    default:
      return base;
  }
}
